import React, { createContext, useContext, useRef, useState } from 'react'
import styled, { css } from 'styled-components/macro'
import palette from 'src/designSystem/variables/palette'
import { ChevronSVG } from 'src/components/Icons/Basic'
import { DropdownItemType } from './FilterDropdown.constants'

/**
 * Pieces shared by every filter menu (the index pages' FilterBar and the dialogs' local
 * filters) for options that hold more options: the children open below the row, as an
 * accordion, rather than in a menu to the side, and a search over the menu reaches
 * into them.
 */

// Set by a menu of filter sections (e.g. "More filters") that searches all of them
// at once; null outside one.
export const FilterMenuSearchContext = createContext<string | null>(null)
export const useFilterMenuSearch = (): string | null =>
  useContext(FilterMenuSearchContext)

// The look of a heading over a group of options: label categories, and the sections
// of an accordion menu (e.g. "More filters").
export const filterHeadingFont = css`
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${palette.neutral['700']};
`

// An accordion section's name: the heading look, with the accordion row's spacing.
export const FilterAccordionHeading = styled.span`
  ${filterHeadingFont}
  flex: 1;
  min-width: 0;
  padding: 4px 0;
`

// Filter menus grow with their options up to this width; longer names are cut off
// with an ellipsis and show in full on hover.
export const FILTER_MENU_MIN_WIDTH = 240
export const FILTER_MENU_MAX_WIDTH = 640

const TruncatedLabel = styled.span`
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

// An option's name, shown in full on hover when the menu is too narrow for it.
export const FilterOptionLabel = ({
  children,
}: {
  children: React.ReactNode
}): JSX.Element => {
  const ref = useRef<HTMLSpanElement>(null)
  const [title, setTitle] = useState<string>()
  return (
    <TruncatedLabel
      ref={ref}
      title={title}
      onMouseEnter={() => {
        const el = ref.current
        setTitle(
          el && el.scrollWidth > el.clientWidth
            ? el.textContent ?? undefined
            : undefined,
        )
      }}
    >
      {children}
    </TruncatedLabel>
  )
}

// A menu with more options than this gets a search box.
export const FILTER_MENU_SEARCH_THRESHOLD = 8

export const countFilterOptions = (values: DropdownItemType[]): number =>
  values.reduce(
    (count, value) =>
      count +
      (value.isGroupHeading ? 0 : 1) +
      countFilterOptions(value.nestedFilter?.filterValues ?? []),
    0,
  )

const labelMatches = (value: DropdownItemType, query: string): boolean =>
  typeof value.label === 'string' && value.label.toLowerCase().includes(query)

/**
 * The options matching a search, at any depth. An option whose own label matches keeps
 * all its children; one that matches only through some children keeps just those. A
 * group heading stays only if something under it does.
 */
export const filterOptionsBySearch = (
  values: DropdownItemType[],
  rawQuery: string,
): DropdownItemType[] => {
  const query = rawQuery.trim().toLowerCase()
  if (!query) return values
  const kept: DropdownItemType[] = []
  let pendingHeading: DropdownItemType | undefined
  values.forEach((value) => {
    if (value.isGroupHeading) {
      pendingHeading = labelMatches(value, query) ? undefined : value
      if (!pendingHeading) kept.push(value)
      return
    }
    let match: DropdownItemType | undefined
    if (labelMatches(value, query)) {
      match = value
    } else if (value.nestedFilter) {
      const children = filterOptionsBySearch(
        value.nestedFilter.filterValues,
        query,
      )
      if (children.length) {
        match = {
          ...value,
          nestedFilter: { ...value.nestedFilter, filterValues: children },
        }
      }
    }
    if (match) {
      if (pendingHeading) {
        kept.push(pendingHeading)
        pendingHeading = undefined
      }
      kept.push(match)
    }
  })
  return kept
}

export const FilterAccordionChevron = ({
  isOpen,
}: {
  isOpen: boolean
}): JSX.Element => (
  <ChevronSVG
    width="12"
    height="12"
    fill="fill.primary.dark"
    style={{
      transform: `rotate(${isOpen ? '90deg' : '0'})`,
      transition: 'transform 0.2s',
      flexShrink: 0,
    }}
  />
)

const AccordionToggleButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  padding: 4px 8px;
  cursor: pointer;
`

// The chevron that opens and closes an option's children.
export const FilterAccordionToggle = ({
  isOpen,
  onToggle,
  label,
}: {
  isOpen: boolean
  onToggle: () => void
  label: string
}): JSX.Element => (
  <AccordionToggleButton
    type="button"
    aria-expanded={isOpen}
    aria-label={`${isOpen ? 'Hide' : 'Show'} ${label} options`}
    onClick={(e) => {
      e.preventDefault()
      e.stopPropagation()
      onToggle()
    }}
  >
    <FilterAccordionChevron isOpen={isOpen} />
  </AccordionToggleButton>
)

// An option's children, indented under it.
export const FilterAccordionBody = styled.div`
  padding-left: 20px;
`
